import { Anchor, Box, Checkbox, Text } from '@mantine/core';
import { splitConsent, type ConsentSetting } from '@creativitas/shared';

/**
 * Текст согласия со ссылками на документы. Тот же разбор, что на регистрации, —
 * в профиле человек видит ровно то предложение, под которым ставил галочку.
 */
export function ConsentText({ consent }: { consent: Pick<ConsentSetting, 'id' | 'text' | 'links'> }) {
  const { pieces } = splitConsent({ ...consent, required: true, revision: '' });
  return (
    <>
      {pieces.map((piece, index) =>
        piece.kind === 'link' ? (
          <Anchor
            key={index}
            href={piece.url}
            target="_blank"
            rel="noopener noreferrer"
            /* Щелчок по ссылке не должен заодно ставить галочку. */
            onClick={(event) => event.stopPropagation()}
          >
            {piece.value}
          </Anchor>
        ) : (
          <span key={index}>{piece.value}</span>
        ),
      )}
    </>
  );
}

/**
 * Согласие со ссылками внутри предложения (§14).
 *
 * Необязательное (рассылки) подписано как необязательное: человек должен
 * видеть, что без этой галочки тоже можно, — иначе согласие на рекламу
 * получено под видом обязательного.
 *
 * Текст и адреса приходят настройкой: это юридические документы, их правит
 * владелец платформы в администрировании, а не разработчик в коде.
 *
 * Ссылки открываются в новой вкладке. Причина не в привычке: человек читает их
 * посреди заполнения формы, и уход со страницы стирал бы уже введённые имя,
 * почту и пароль. Документы к тому же пока возвращают 404 — тем более незачем
 * терять из-за них заполненную форму.
 */
export function ConsentCheckbox({
  consent,
  checked,
  onChange,
  error,
}: {
  consent: ConsentSetting;
  checked: boolean;
  onChange: (checked: boolean) => void;
  error?: boolean;
}) {
  /*
    Метка — на всём согласии, а не на самом поле: Mantine вешает лишние
    атрибуты на `input`, и по такой метке не найти ни ссылки, ни текст рядом.
    А проверять нужно именно их: ссылка ведёт на юридический документ.
  */
  return (
    <Box data-testid={`consent-${consent.id}`}>
      <Checkbox
        checked={checked}
        onChange={(event) => onChange(event.currentTarget.checked)}
        error={error}
        /*
          Не `wrapperProps`: длинное предложение с тремя ссылками должно
          переноситься и оставаться кликабельным по самому тексту, а не только по
          квадратику. Поэтому подпись — обычный текст рядом с полем.
        */
        label={
          <Text size="sm" style={{ lineHeight: 1.45 }}>
            <ConsentText consent={consent} />
            {consent.required ? null : (
              <Text span size="xs" c="var(--text-muted)">
                {' '}
                (по желанию — включить или выключить можно в профиле)
              </Text>
            )}
          </Text>
        }
      />
    </Box>
  );
}
